import { useCallback, useEffect, useState } from 'react';

/**
 * Persisted piece of UI state. `parse` validates whatever is in storage so a stale or
 * hand-edited value can never poison the app.
 */
export function useLocalStorage<T>(
  key: string,
  fallback: T,
  parse: (raw: string) => T | null,
  serialize: (value: T) => string = String,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return fallback;
      return parse(raw) ?? fallback;
    } catch {
      return fallback;
    }
  });

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== key || event.newValue === null) return;
      const parsed = parse(event.newValue);
      if (parsed !== null) setValue(parsed);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key, parse]);

  const update = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, serialize(next));
      } catch {
        /* Persisting is best-effort; the in-memory value still updates. */
      }
    },
    [key, serialize],
  );

  return [value, update];
}
